import { useEffect, useState } from "react";
import { FastAverageColor } from "fast-average-color";

interface UseImageDominantColorOptions {
  enabled?: boolean;
  fallbackColor?: string;
  imageUrl?: string;
}

interface DominantColorResult {
  color: string;
  error: Error | null;
  isLoading: boolean;
  rgba: string;
}

const fac = new FastAverageColor();

const colorCache = new Map<string, { hex: string; rgba: string }>();

const colorToRgba = (color: string, alpha = 1): string => {
  if (color.startsWith("rgba(")) {
    return color;
  }
  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }

  if (color.startsWith("hsl(") || color.startsWith("hsla(")) {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  const cleanHex = color.replace("#", "");

  let r: number, g: number, b: number;
  if (cleanHex.length === 3) {
    r = Number.parseInt(cleanHex[0] + cleanHex[0], 16);
    g = Number.parseInt(cleanHex[1] + cleanHex[1], 16);
    b = Number.parseInt(cleanHex[2] + cleanHex[2], 16);
  } else {
    r = Number.parseInt(cleanHex.slice(0, 2), 16);
    g = Number.parseInt(cleanHex.slice(2, 4), 16);
    b = Number.parseInt(cleanHex.slice(4, 6), 16);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const useImageDominantColor = ({
  imageUrl,
  fallbackColor = "transparent",
  enabled = true,
}: UseImageDominantColorOptions): DominantColorResult => {
  const [color, setColor] = useState<string>(fallbackColor);
  const [rgba, setRgba] = useState<string>(() => colorToRgba(fallbackColor));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!imageUrl || !enabled) {
      setColor(fallbackColor);
      setRgba(colorToRgba(fallbackColor));
      return;
    }

    const cachedColors = colorCache.get(imageUrl);
    if (cachedColors) {
      setColor(cachedColors.hex);
      setRgba(cachedColors.rgba);
      return;
    }

    let isCancelled = false;

    const extractColor = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fac.getColorAsync(imageUrl);

        if (!isCancelled) {
          const extractedColor = result.hex;
          const extractedRgba = result.rgba;

          colorCache.set(imageUrl, {
            hex: extractedColor,
            rgba: extractedRgba,
          });
          setColor(extractedColor);
          setRgba(extractedRgba);
        }
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          const errorMessage =
            err instanceof Error ? err : new Error("Failed to extract color");
          setError(errorMessage);
          setColor(fallbackColor);
          setRgba(colorToRgba(fallbackColor));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void extractColor();

    return () => {
      isCancelled = true;
    };
  }, [imageUrl, fallbackColor, enabled]);

  return { color, rgba, isLoading, error };
};
